// A goal has "reached cap" once its Client Accepted count meets or exceeds
// its own ordered quantity — same guard (quantity_ordered must be set and
// positive) the backend's check_goal_cap() QA check uses, so this can never
// flag a goal that check itself would skip.
export function isGoalOverCap(g) {
  return !!(g.quantity_ordered > 0 && (g.client_accepted_count || 0) >= g.quantity_ordered);
}

// Maps GET /goals/?...&include_counts=true's raw per-goal rows into
// StatusBreakdownTable's { key, label, counts, ...extra } shape — grouped by
// component (program-level selection) or one row per goal (component-level
// selection). Shared by LeadQA.js's Component/Goal Rollup and My Home's
// "Your Programs" expansion — extracted here once a second consumer needed
// the identical aggregation, rather than a second hand-copy.
//
// `overCapGoals` (added for the Lead QA rollup's cap-warning indicator) is
// every underlying goal that's individually reached its own cap, contributing
// to this row — at Component level (one row per goal) this is either empty
// or the single goal itself; at Program level (one row per component) it can
// list several sibling goals independently over cap, which is exactly the
// case a Component Rollup's combined total otherwise masks (e.g. a component
// whose 3 goals are 9/84, 0/84, and 141/85 rolls up to 150/253 — nowhere near
// "full" — while the third goal is individually 166% over).
export function goalsToStatusBreakdownRows(rollupGoals, isProgram, allComponents) {
  const rowMap = {};
  rollupGoals.forEach(g => {
    const key = isProgram ? g.component_id : g.id;
    const label = isProgram
      ? (allComponents.find(c => String(c.id) === String(g.component_id))?.component_name || 'Unknown')
      : (g.goal_name || '—');
    if (!rowMap[key]) {
      rowMap[key] = {
        key: String(key), label, qty: 0, rejectedClient: 0, rejectedDa: 0,
        counts: {
          'Pending Review': 0, 'Under Review': 0, 'Approved Pending Call Review': 0,
          'DemandArc Accepted': 0, 'Client Accepted': 0, 'Rejected': 0,
        },
        overCapGoals: [],
      };
    }
    const r = rowMap[key];
    r.qty += g.quantity_ordered || 0;
    r.counts['Pending Review'] += g.pending_review_count || 0;
    r.counts['Under Review'] += g.under_review_count || 0;
    r.counts['Approved Pending Call Review'] += g.approved_pending_call_review_count || 0;
    r.counts['DemandArc Accepted'] += g.da_accepted_count || 0;
    r.counts['Client Accepted'] += g.client_accepted_count || 0;
    r.counts['Rejected'] += g.rejected_count || 0;
    r.rejectedClient += g.rejected_client_count || 0;
    r.rejectedDa += g.rejected_da_count || 0;
    if (isGoalOverCap(g)) {
      r.overCapGoals.push({
        goalId: g.id,
        goalName: g.goal_name || label,
        accepted: g.client_accepted_count || 0,
        ordered: g.quantity_ordered,
      });
    }
  });
  return Object.values(rowMap).sort((a, b) => a.label.localeCompare(b.label));
}

// One row PER GOAL (never aggregated by component) — the row label is the
// goal's OWNING COMPONENT name, with the goal's own name as a separate
// column (see ProgramCarousel.js's EXTRA_COLUMNS: Goal/Target/Total).
// Used by the unified "Assigned Programs & Lead Status" card (My Home PM +
// Supplier Portal) so a PM sees the same goal-level detail Supplier Portal
// already showed — 2026-08-31, replacing goalsToStatusBreakdownRows's
// component-level aggregation for that one use, which lost exactly this
// detail. `total` (not tracked by the aggregated function above) is summed
// across all 6 status buckets — "how many leads have landed on this goal
// so far, in any state."
export function goalsToPerGoalRows(rollupGoals, allComponents) {
  return rollupGoals.map(g => {
    const componentName = allComponents.find(c => String(c.id) === String(g.component_id))?.component_name || 'Unknown';
    const counts = {
      'Pending Review': g.pending_review_count || 0,
      'Under Review': g.under_review_count || 0,
      'Approved Pending Call Review': g.approved_pending_call_review_count || 0,
      'DemandArc Accepted': g.da_accepted_count || 0,
      'Client Accepted': g.client_accepted_count || 0,
      'Rejected': g.rejected_count || 0,
    };
    return {
      key: String(g.id),
      label: componentName,
      goalName: g.goal_name || '—',
      qty: g.quantity_ordered || 0,
      total: Object.values(counts).reduce((s, n) => s + n, 0),
      counts,
      rejectedClient: g.rejected_client_count || 0,
      rejectedDa: g.rejected_da_count || 0,
    };
  }).sort((a, b) => a.label.localeCompare(b.label) || (a.goalName || '').localeCompare(b.goalName || ''));
}

// Same per-goal row shape as goalsToPerGoalRows, sourced from
// GET /supplier-deliveries/status's own per-program `goals[]` array instead
// — that shape already carries `component_name` directly and a ready
// `lead_counts` map (no separate count fields to normalize, no
// `allComponents` lookup needed), so this is a reshape, not an aggregation.
export function supplierGoalsToRows(goals) {
  return (goals || []).map(g => ({
    key: String(g.goal_id),
    label: g.component_name,
    goalName: g.goal_name,
    qty: g.quantity_ordered,
    total: g.total,
    counts: g.lead_counts,
  })).sort((a, b) => (a.label || '').localeCompare(b.label || '') || (a.goalName || '').localeCompare(b.goalName || ''));
}
